import { gql } from "@apollo/client";
import { format, isValid, parseISO, subMonths } from "date-fns";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { DATE_FORMAT, PAGE_SIZE } from "../_lib/constants";
import { RegisterTableHeader } from "../_lib/register-table-header";
import { useRegisterRecordsLazyQuery } from "./__generated__/page.gql";
import { PatientRegisterTable } from "./_lib/patient-register-table";

gql`
  query RegisterRecords($input: RegisterRecordsInput!) {
    registerRecords(input: $input) {
      total
      limit
      offset
      records {
        _id
        date
        patient {
          _id
          patientUuid
          name
          dateOfBirth
          gender
          address {
            street
            ward
            subCounty
            state
          }
          phoneNumber {
            value
            ownership
          }
        }
        careActivities {
          _id
          type
          careActivity {
            __typename
            ... on DiagnosisReceivedDetails {
              name
            }
            ... on InvestigationRequestedDetails {
              investigationId
              name
            }
            ... on InvestigationPerformedDetails {
              investigationId
              name
            }
            ... on PrescriptionIssuedDetails {
              id
              medication {
                name
              }
            }
          }
        }
      }
    }
  }
`;

export const PatientRegister = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const startDateParam = searchParams.get("startDate") ?? "";
  const endDateParam = searchParams.get("endDate") ?? "";
  const currentPageParam = Number(searchParams.get("currentPage"));
  const [currentPage, setCurrentPage] = useState(
    !Number.isNaN(currentPageParam) ? currentPageParam : 0,
  );
  const [startDate, setStartDate] = useState(
    isValid(parseISO(startDateParam))
      ? startDateParam
      : format(subMonths(new Date(), 1), DATE_FORMAT),
  );
  const [endDate, setEndDate] = useState(
    isValid(parseISO(endDateParam))
      ? endDateParam
      : format(new Date(), DATE_FORMAT),
  );

  const [fetchRecords, { data, loading }] = useRegisterRecordsLazyQuery();

  useEffect(() => {
    if (startDate && endDate) {
      fetchRecords({
        variables: {
          input: {
            filterOptions: {
              startDate,
              endDate,
            },
            paginationOptions: {
              offset: currentPage * PAGE_SIZE,
              limit: PAGE_SIZE,
            },
          },
        },
      });
    }
  }, [startDate, endDate, currentPage, fetchRecords]);

  useEffect(() => {
    setSearchParams({ startDate, endDate, currentPage: `${currentPage}` });
  }, [startDate, endDate, currentPage, setSearchParams]);

  return (
    <>
      <RegisterTableHeader
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        setCurrentPage={setCurrentPage}
        total={data?.registerRecords?.total ?? 0}
        heading={
          <FormattedMessage
            id="register.title"
            defaultMessage="Patient Register"
          />
        }
      />
      <PatientRegisterTable
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        data={data}
        loading={loading}
      />
    </>
  );
};
